import { getPartesFilterDay, restoreDailyPartesFilters } from "../../utils/dailyPartesFilters";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	FormControl,
	Grid,
	InputLabel,
	LinearProgress,
	MenuItem,
	Pagination,
	Select,
	Typography,
} from "@mui/material";
import { saveAs } from "file-saver";
import { useLocation, useNavigate } from "react-router-dom";

import { SubHeader } from "../../utils/componentes-utiles/SubHeader";
import {
	actualizarPartesOperativos,
	getPartesOperativos,
	getUsuarioPreferencias,
	saveUsuarioPreferencias,
} from "../../services/apis";
import { createPartesOperativosExcelWorkbook } from "../../utils/exportPartesOperativosExcel";
import { OperativosAdvancedFilters } from "./operativos/OperativosAdvancedFilters";
import { OperativosCaboAccordion } from "./operativos/OperativosCaboAccordion";
import { OperativosFilters } from "./operativos/OperativosFilters";
import { OperativosSelectionSummary } from "./operativos/OperativosSelectionSummary";
import { getAuthStorageUserScope } from "../../services/authStorage";
import {
	formatDisplayDate,
	getDefaultFechaDesde,
	getDefaultFechaHasta,
	shiftIsoDateByDays,
} from "./operativos/operativosUtils";

const OPERATIVOS_SESSION_STATE_KEY = "planaett:operativos:session-state";
const OPERATIVOS_ADVANCED_FILTERS_SESSION_KEY = "planaett:operativos:advanced-filters";
const PREF_OPERATIVOS_SESSION_STATE = "operativos.session-state";
const PREF_OPERATIVOS_ADVANCED_FILTERS = "operativos.advanced-filters";
const buildUserSessionKey = (baseKey) =>
	`${baseKey}:${encodeURIComponent(getAuthStorageUserScope())}`;

const readUserScopedSessionValue = (baseKey) => {
	const scopedValue = window.sessionStorage.getItem(buildUserSessionKey(baseKey));
	return scopedValue !== null ? scopedValue : window.sessionStorage.getItem(baseKey);
};

const writeUserScopedSessionValue = (baseKey, value) => {
	window.sessionStorage.setItem(buildUserSessionKey(baseKey), value);
};

const OPERATIVOS_ALLOWED_ADVANCED_FILTER_KEYS = new Set([
	"campanas",
	"empresas",
	"delegaciones",
	"cabos",
	"validado",
	"revisado",
]);

const normalizeOperativosStatusFilter = (value) =>
	["todos", "si", "no"].includes(String(value || "").trim())
		? String(value).trim()
		: "todos";

const normalizeOperativosAdvancedFilters = (filters = []) =>
	(Array.isArray(filters) ? filters : [])
		.filter(
			(filterItem) =>
				typeof filterItem?.key === "string" &&
				OPERATIVOS_ALLOWED_ADVANCED_FILTER_KEYS.has(filterItem.key)
		)
		.map((filterItem) => ({
			key: filterItem.key,
			seleccionado: Array.isArray(filterItem?.seleccionado)
				? filterItem.seleccionado.map((value) => String(value))
				: [],
		}));

const parteMatchesSearchTerm = (parte, rawTerm) => {
	const termino = String(rawTerm || "").trim().toLowerCase();

	if (!termino) {
		return true;
	}

	const campos = [
		parte?.id_cuadrilla,
		parte?.nombre_cuadrilla,
		parte?.nombre_cliente,
		parte?.nombre_delegacion,
		parte?.nombre_campana,
		parte?.nombre_cabo,
		parte?.apellidos_cabo,
		parte?.nif_cabo,
	];

	return campos.some((campo) =>
		String(campo ?? "").toLowerCase().includes(termino)
	);
};

const readOperativosSessionState = () => {
	try {
		const rawValue = readUserScopedSessionValue(OPERATIVOS_SESSION_STATE_KEY);
		if (!rawValue) return null;

		const parsedValue = JSON.parse(rawValue);
		return parsedValue && typeof parsedValue === "object" ? parsedValue : null;
	} catch (error) {
		return null;
	}
};

const persistOperativosSessionState = (state) => {
	try {
		writeUserScopedSessionValue(OPERATIVOS_SESSION_STATE_KEY, JSON.stringify(state));
	} catch (error) {
		// Ignore session persistence failures in the UI.
	}
};

const readOperativosAdvancedFiltersSessionState = () => {
	try {
		const rawValue = readUserScopedSessionValue(OPERATIVOS_ADVANCED_FILTERS_SESSION_KEY);
		if (!rawValue) return [];

		const parsedValue = JSON.parse(rawValue);
		return Array.isArray(parsedValue) ? parsedValue : [];
	} catch (error) {
		return [];
	}
};

const buildOperativoCaboFilterValue = (parte) => {
	const caboNombre =
		`${parte?.apellidos_cabo || ""} ${parte?.nombre_cabo || ""}`.trim() || "Sin cabo";
	const caboNif = String(parte?.nif_cabo || "").trim();
	const caboId = Number(parte?.id_cabo || 0);

	if (caboId > 0) {
		return String(caboId);
	}

	if (caboNif !== "") {
		return `nif:${caboNif}`;
	}

	return `cabo:${caboNombre}`;
};

const matchesAdvancedOperativosFilter = (parte, filterKey, selectedValues = []) => {
	const values = (Array.isArray(selectedValues) ? selectedValues : []).map((value) =>
		String(value)
	);

	if (values.length === 0) {
		return true;
	}

	switch (filterKey) {
		case "campanas": {
			const idCampana = Number(parte?.id_campana || 0);
			const value = idCampana > 0 ? String(idCampana) : "__sin_campana__";
			return values.includes(value);
		}

		case "empresas": {
			const idClienteG = String(parte?.id_clienteG || "").trim();
			const nombreCliente = String(parte?.nombre_cliente || "").trim();
			const value = idClienteG !== "" ? idClienteG : `empresa:${nombreCliente || "sin_empresa"}`;
			return values.includes(value);
		}

		case "delegaciones": {
			const idDelegacion = Number(parte?.id_delegacion || 0);
			const value = idDelegacion > 0 ? String(idDelegacion) : "__sin_delegacion__";
			return values.includes(value);
		}

		case "cabos":
			return values.includes(buildOperativoCaboFilterValue(parte));

		case "validado":
			return values.includes(String(Number(parte?.validado || 0)));

		case "revisado":
			return values.includes(String(Number(parte?.revisado || 0)));

		default:
			return true;
	}
};

const buildInitialSelectedPartes = (items = []) => {
	const nextSelectedPartes = {};

	items.forEach((parte) => {
		const idCuadrilla = Number(parte?.id_cuadrilla || 0);
		if (idCuadrilla > 0 && Number(parte?.operativo || 0) === 1) {
			nextSelectedPartes[idCuadrilla] = true;
		}
	});

	return nextSelectedPartes;
};

const buildOperativosRestoreState = ({ routeState = null, pageState = null } = {}) => ({
	...(pageState || {}),
	...(routeState || {}),
	advancedFilters: Object.prototype.hasOwnProperty.call(routeState || {}, "advancedFilters")
		? routeState.advancedFilters
		: pageState?.advancedFilters,
	filtroAplicado: {
		...(pageState?.filtroAplicado || {}),
		...(routeState?.filtroAplicado || {}),
	},
});

export const PanelOperativos = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const latestRequestRef = useRef(0);
	const routeRestoreState = location.state?.operativosReturnState || null;
	const pageRestoreState = readOperativosSessionState();
	const persistedAdvancedFilters = readOperativosAdvancedFiltersSessionState();
	const [filterDay] = useState(getPartesFilterDay);
	const mergedRestoreState = restoreDailyPartesFilters(buildOperativosRestoreState({
		routeState: routeRestoreState,
		pageState: pageRestoreState,
	}), filterDay);
	const restoreAdvancedFilters = normalizeOperativosAdvancedFilters(
		Array.isArray(mergedRestoreState?.advancedFilters)
			? mergedRestoreState.advancedFilters
			: persistedAdvancedFilters
	);

	const [itemsPerPage, setItemsPerPage] = useState(Number(mergedRestoreState?.itemsPerPage || 25));
	const [currentPage, setCurrentPage] = useState(Number(mergedRestoreState?.currentPage || 1));
	const [textoBusqueda, setTextoBusqueda] = useState(mergedRestoreState?.textoBusqueda || "");
	const [filtroOperativo, setFiltroOperativo] = useState(
		normalizeOperativosStatusFilter(mergedRestoreState?.filtroOperativo || "todos")
	);
	const [fechaDesde, setFechaDesde] = useState(
		mergedRestoreState?.fechaDesde || getDefaultFechaDesde()
	);
	const [fechaHasta, setFechaHasta] = useState(
		mergedRestoreState?.fechaHasta || getDefaultFechaHasta()
	);
	const [filtroAplicado, setFiltroAplicado] = useState({
		fechaDesde:
			mergedRestoreState?.filtroAplicado?.fechaDesde ||
			mergedRestoreState?.fechaDesde ||
			getDefaultFechaDesde(),
		fechaHasta:
			mergedRestoreState?.filtroAplicado?.fechaHasta ||
			mergedRestoreState?.fechaHasta ||
			getDefaultFechaHasta(),
	});
	const [partes, setPartes] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const [saving, setSaving] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [errorFechas, setErrorFechas] = useState("");
	const [feedback, setFeedback] = useState(null);
	const [selectedPartes, setSelectedPartes] = useState({});
	const [expandedCabos, setExpandedCabos] = useState({});
	const [openFiltros, setOpenFiltros] = useState(Boolean(mergedRestoreState?.openFiltros));
	const [cantidadFiltros, setCantidadFiltros] = useState(0);
	const [filtrosAvanzados, setFiltrosAvanzados] = useState(
		restoreAdvancedFilters
	);
	const [resetFiltrosAvanzadosToken, setResetFiltrosAvanzadosToken] = useState(0);
	const [remotePreferencesReady, setRemotePreferencesReady] = useState(false);

	const persistOperativoChange = async (
		idsCuadrilla,
		checked,
		previousSelectedPartes,
		previousPartes,
		successMessage
	) => {
		if (!Array.isArray(idsCuadrilla) || idsCuadrilla.length === 0) {
			return;
		}

		setSaving(true);
		setFeedback(null);

		try {
			const response = await actualizarPartesOperativos({
				ids_cuadrilla: idsCuadrilla,
				operativo: checked ? 1 : 0,
			});

			if (response?.success !== true) {
				throw new Error(response?.message || "No se pudieron actualizar los partes.");
			}

			setFeedback({
				severity: "success",
				message:
					successMessage ||
					`${idsCuadrilla.length} parte(s) marcados como ${checked ? "operativos" : "no operativos"}.`,
			});
		} catch (error) {
			console.error("Error actualizando operativos:", error);
			setSelectedPartes(previousSelectedPartes);
			setPartes(previousPartes);
			setFeedback({
				severity: "error",
				message: error?.message || "No se pudieron guardar los cambios de operativos.",
			});
		} finally {
			setSaving(false);
		}
	};

	const cargarPartes = async (desde, hasta) => {
		const requestId = latestRequestRef.current + 1;
		latestRequestRef.current = requestId;

		try {
			setLoaded(false);
			setErrorFechas("");
			setFeedback(null);

			if (!desde || !hasta) {
				setPartes([]);
				setErrorFechas("Debes indicar ambas fechas.");
				setLoaded(true);
				return;
			}

			if (desde > hasta) {
				setPartes([]);
				setErrorFechas("La fecha desde no puede ser mayor que la fecha hasta.");
				setLoaded(true);
				return;
			}

			const response = await getPartesOperativos({
				fechaDesde: desde,
				fechaHasta: hasta,
			});

			if (latestRequestRef.current !== requestId) {
				return;
			}

			if (response?.success === true) {
				const nextPartes = Array.isArray(response?.data) ? response.data : [];
				const nextSelectedPartes = buildInitialSelectedPartes(nextPartes);

				setPartes(nextPartes);
				setSelectedPartes(nextSelectedPartes);
				setExpandedCabos({});
				return;
			}

			setPartes([]);
			setFeedback({
				severity: "warning",
				message: response?.message || "No se pudieron cargar los partes.",
			});
		} catch (error) {
			if (latestRequestRef.current !== requestId) {
				return;
			}

			console.error("Error cargando panel de operativos:", error);
			setPartes([]);
			setFeedback({
				severity: "error",
				message: "No se pudieron cargar los partes operativos.",
			});
		} finally {
			if (latestRequestRef.current === requestId) {
				setLoaded(true);
			}
		}
	};

	useEffect(() => {
		cargarPartes(filtroAplicado.fechaDesde, filtroAplicado.fechaHasta);
	}, [filtroAplicado]);

	useEffect(() => {
		setCurrentPage(1);
	}, [textoBusqueda, filtroOperativo, filtrosAvanzados]);

	useEffect(() => {
		persistOperativosSessionState({
			filterDay,
			textoBusqueda,
			fechaDesde,
			fechaHasta,
			filtroAplicado,
			filtroOperativo,
			itemsPerPage,
			currentPage,
			openFiltros,
			advancedFilters: filtrosAvanzados,
		});
	}, [
		currentPage,
		fechaDesde,
		fechaHasta,
		filtroAplicado,
		filtrosAvanzados,
		filtroOperativo,
		itemsPerPage,
		openFiltros,
		textoBusqueda,
	]);

	useEffect(() => {
		let active = true;

		const loadRemotePreferences = async () => {
			try {
				const response = await getUsuarioPreferencias([
					PREF_OPERATIVOS_SESSION_STATE,
					PREF_OPERATIVOS_ADVANCED_FILTERS,
				]);
				if (!active || response?.success !== true) {
					return;
				}

				const preferences = response?.preferences || {};
				const remoteSession = restoreDailyPartesFilters(preferences[PREF_OPERATIVOS_SESSION_STATE], filterDay);
				const remoteAdvancedFilters = preferences[PREF_OPERATIVOS_ADVANCED_FILTERS];

				if (remoteSession && typeof remoteSession === "object") {
					if (typeof remoteSession.textoBusqueda === "string") setTextoBusqueda(remoteSession.textoBusqueda);
					if (remoteSession.fechaDesde) setFechaDesde(remoteSession.fechaDesde);
					if (remoteSession.fechaHasta) setFechaHasta(remoteSession.fechaHasta);
					if (remoteSession.filtroAplicado && typeof remoteSession.filtroAplicado === "object") {
						setFiltroAplicado(remoteSession.filtroAplicado);
					}
					if (remoteSession.filtroOperativo) {
						setFiltroOperativo(normalizeOperativosStatusFilter(remoteSession.filtroOperativo));
					}
					if (remoteSession.itemsPerPage) setItemsPerPage(Number(remoteSession.itemsPerPage || 25));
					if (remoteSession.currentPage) setCurrentPage(Number(remoteSession.currentPage || 1));
					if (Object.prototype.hasOwnProperty.call(remoteSession, "openFiltros")) {
						setOpenFiltros(Boolean(remoteSession.openFiltros));
					}
				}

				if (Array.isArray(remoteAdvancedFilters)) {
					setFiltrosAvanzados(normalizeOperativosAdvancedFilters(remoteAdvancedFilters));
				}
			} catch (error) {
				console.error("Error cargando preferencias remotas de operativos:", error);
			} finally {
				if (active) {
					setRemotePreferencesReady(true);
				}
			}
		};

		loadRemotePreferences();

		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		if (!remotePreferencesReady) {
			return;
		}

		saveUsuarioPreferencias({
			[PREF_OPERATIVOS_SESSION_STATE]: {
				filterDay,
				textoBusqueda,
				fechaDesde,
				fechaHasta,
				filtroAplicado,
				filtroOperativo,
				itemsPerPage,
				currentPage,
				openFiltros,
			},
			[PREF_OPERATIVOS_ADVANCED_FILTERS]: filtrosAvanzados,
		}).catch((error) => {
			console.error("Error guardando preferencias remotas de operativos:", error);
		});
	}, [
		currentPage,
		fechaDesde,
		fechaHasta,
		filtroAplicado,
		filtroOperativo,
		filtrosAvanzados,
		itemsPerPage,
		openFiltros,
		remotePreferencesReady,
		textoBusqueda,
	]);

	const aplicarFiltroFechas = () => {
		if (!fechaDesde || !fechaHasta) {
			setErrorFechas("Debes indicar ambas fechas.");
			return;
		}

		if (fechaDesde > fechaHasta) {
			setErrorFechas("La fecha desde no puede ser mayor que la fecha hasta.");
			return;
		}

		setErrorFechas("");
		setCurrentPage(1);
		setFiltroAplicado({
			fechaDesde,
			fechaHasta,
		});
	};

	const limpiarFiltros = () => {
		const desde = getDefaultFechaDesde();
		const hasta = getDefaultFechaHasta();

		setFechaDesde(desde);
		setFechaHasta(hasta);
		setTextoBusqueda("");
		setFiltroOperativo("todos");
		setErrorFechas("");
		setCurrentPage(1);
		setFiltrosAvanzados((currentFilters) =>
			(Array.isArray(currentFilters) ? currentFilters : []).map((filterItem) => ({
				...filterItem,
				seleccionado: [],
			}))
		);
		setResetFiltrosAvanzadosToken((currentToken) => currentToken + 1);
		setFiltroAplicado({
			fechaDesde: desde,
			fechaHasta: hasta,
		});
	};

	const moverRangoFechas = (days) => {
		setFechaDesde((current) => shiftIsoDateByDays(current, days));
		setFechaHasta((current) => shiftIsoDateByDays(current, days));
	};

	const partesFiltrados = useMemo(() => {
		const filtrosActivos = filtrosAvanzados.filter(
			(filtro) => Array.isArray(filtro?.seleccionado) && filtro.seleccionado.length > 0
		);

		return partes.filter((parte) => {
			const cumpleOperativo =
				filtroOperativo === "todos" ||
				(filtroOperativo === "si" && Number(parte?.operativo || 0) === 1) ||
				(filtroOperativo === "no" && Number(parte?.operativo || 0) !== 1);

			if (!cumpleOperativo) {
				return false;
			}

			const cumpleFiltrosAvanzados = filtrosActivos.every((filtro) =>
				matchesAdvancedOperativosFilter(parte, filtro.key, filtro.seleccionado)
			);

			if (!cumpleFiltrosAvanzados) {
				return false;
			}

			return parteMatchesSearchTerm(parte, textoBusqueda);
		});
	}, [filtroOperativo, filtrosAvanzados, partes, textoBusqueda]);

	const partesOperativosExportables = useMemo(
		() =>
			partesFiltrados.filter(
				(parte) =>
					Number(parte?.operativo || 0) === 1
			),
		[partesFiltrados]
	);

	const partesAgrupadosPorCabo = useMemo(() => {
		const groups = new Map();

		partesFiltrados.forEach((parte) => {
			const caboNombre =
				`${parte?.apellidos_cabo || ""} ${parte?.nombre_cabo || ""}`.trim() || "Sin cabo";
			const caboNif = String(parte?.nif_cabo || "").trim();
			const caboId = Number(parte?.id_cabo || 0);
			const groupKey = `${caboId}-${caboNombre}-${caboNif}`;
			const currentGroup = groups.get(groupKey) || {
				id: groupKey,
				id_cabo: caboId,
				nombre: caboNombre,
				nif_cabo: caboNif,
				partes: [],
			};

			currentGroup.partes.push(parte);
			groups.set(groupKey, currentGroup);
		});

		return Array.from(groups.values()).sort((left, right) =>
			left.nombre.localeCompare(right.nombre, "es")
		);
	}, [partesFiltrados]);

	const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentCaboGroups = partesAgrupadosPorCabo.slice(indexOfFirstItem, indexOfLastItem);
	const currentPartes = useMemo(
		() => currentCaboGroups.flatMap((group) => group.partes),
		[currentCaboGroups]
	);

	const allCurrentPageSelected = useMemo(() => {
		if (currentPartes.length === 0) {
			return false;
		}

		return currentPartes.every((parte) => Boolean(selectedPartes[parte?.id_cuadrilla]));
	}, [currentPartes, selectedPartes]);

	const someCurrentPageSelected = useMemo(
		() => currentPartes.some((parte) => Boolean(selectedPartes[parte?.id_cuadrilla])),
		[currentPartes, selectedPartes]
	);

	const toggleParteSelection = async (idCuadrilla) => {
		if (!idCuadrilla || saving) {
			return;
		}

		const nextChecked = !Boolean(selectedPartes[idCuadrilla]);
		const previousSelectedPartes = selectedPartes;
		const previousPartes = partes;

		setSelectedPartes((current) => ({
			...current,
			[idCuadrilla]: nextChecked,
		}));
		setPartes((current) =>
			current.map((parte) =>
				Number(parte?.id_cuadrilla || 0) === idCuadrilla
					? { ...parte, operativo: nextChecked ? 1 : 0 }
					: parte
			)
		);

		await persistOperativoChange(
			[idCuadrilla],
			nextChecked,
			previousSelectedPartes,
			previousPartes,
			`Parte ${idCuadrilla} marcado como ${nextChecked ? "operativo" : "no operativo"}.`
		);
	};

	const toggleCurrentPageSelection = async (checked) => {
		if (saving) {
			return;
		}

		const idsCuadrilla = currentPartes
			.map((parte) => Number(parte?.id_cuadrilla || 0))
			.filter((idCuadrilla) => idCuadrilla > 0 && Boolean(selectedPartes[idCuadrilla]) !== checked);

		if (idsCuadrilla.length === 0) {
			return;
		}

		const idsSet = new Set(idsCuadrilla);
		const previousSelectedPartes = selectedPartes;
		const previousPartes = partes;

		setSelectedPartes((current) => {
			const next = { ...current };

			idsCuadrilla.forEach((idCuadrilla) => {
				next[idCuadrilla] = checked;
			});

			return next;
		});
		setPartes((current) =>
			current.map((parte) =>
				idsSet.has(Number(parte?.id_cuadrilla || 0))
					? { ...parte, operativo: checked ? 1 : 0 }
					: parte
			)
		);

		await persistOperativoChange(
			idsCuadrilla,
			checked,
			previousSelectedPartes,
			previousPartes,
			`${idsCuadrilla.length} parte(s) de la pagina actual marcados como ${checked ? "operativos" : "no operativos"}.`
		);
	};

	const toggleCaboSelection = async (group, checked) => {
		if (saving) {
			return;
		}

		const idsCuadrilla = group.partes
			.map((parte) => Number(parte?.id_cuadrilla || 0))
			.filter((idCuadrilla) => idCuadrilla > 0 && Boolean(selectedPartes[idCuadrilla]) !== checked);

		if (idsCuadrilla.length === 0) {
			return;
		}

		const idsSet = new Set(idsCuadrilla);
		const previousSelectedPartes = selectedPartes;
		const previousPartes = partes;

		setSelectedPartes((current) => {
			const next = { ...current };

			idsCuadrilla.forEach((idCuadrilla) => {
				next[idCuadrilla] = checked;
			});

			return next;
		});
		setPartes((current) =>
			current.map((parte) =>
				idsSet.has(Number(parte?.id_cuadrilla || 0))
					? { ...parte, operativo: checked ? 1 : 0 }
					: parte
			)
		);

		await persistOperativoChange(
			idsCuadrilla,
			checked,
			previousSelectedPartes,
			previousPartes,
			`${idsCuadrilla.length} parte(s) de ${group.nombre} marcados como ${checked ? "operativos" : "no operativos"}.`
		);
	};

	const toggleExpandedCabo = (groupId, isExpanded) => {
		setExpandedCabos((current) => ({
			...current,
			[groupId]: isExpanded,
		}));
	};

	const exportarPartesOperativos = async () => {
		if (exporting) {
			return;
		}

		if (partesOperativosExportables.length === 0) {
			setFeedback({
				severity: "warning",
				message: "No hay partes operativos para exportar con los filtros actuales.",
			});
			return;
		}

		setExporting(true);
		setFeedback(null);

		try {
			const workbook = await createPartesOperativosExcelWorkbook({
				rows: partesOperativosExportables.map((parte) => ({
					fecha: formatDisplayDate(parte?.fecha_cuadrilla || ""),
					rfcte: String(parte?.rfcte || ""),
					nombreEmpresa: String(parte?.nombre_cliente || ""),
					totalPersonas: Number(parte?.total_recolectores || 0),
					nifCabo: String(parte?.nif_cabo || ""),
					nombreCabo:
						`${parte?.apellidos_cabo || ""} ${parte?.nombre_cabo || ""}`.trim(),
				})),
			});
			const excelBuffer = await workbook.xlsx.writeBuffer();
			const blob = new Blob([excelBuffer], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});

			saveAs(
				blob,
				`partes-operativos-${filtroAplicado.fechaDesde}-${filtroAplicado.fechaHasta}.xlsx`
			);

			setFeedback({
				severity: "success",
				message: `${partesOperativosExportables.length} parte(s) operativos exportados a Excel.`,
			});
		} catch (error) {
			console.error("Error exportando partes operativos:", error);
			setFeedback({
				severity: "error",
				message: "No se pudo generar el Excel de partes operativos.",
			});
		} finally {
			setExporting(false);
		}
	};

	const toggleFiltros = () => {
		setOpenFiltros((currentOpen) => !currentOpen);
	};

	const cerrarFiltros = () => {
		setOpenFiltros(false);
	};

	const volverACuadrillas = () => {
		navigate("/cuadrillas", {
			state: {
				cuadrillasReturnState: {
					textoBusqueda,
					fechaDesde,
					fechaHasta,
					filtroAplicado,
					filtroOperativo,
					openFiltros,
					advancedFilters: filtrosAvanzados,
				},
			},
		});
	};

	return (
		<Grid container direction="row" justifyContent="center" alignItems="center" size={{ xs: 12 }}>
			<Grid
				container
				direction="column"
				justifyContent="start"
				alignItems="start"
				size={{ xs: 12, md: 11.5 }}
				sx={{ mb: 1 }}
			>
				<Grid item size={{ xs: 12 }}>
					<SubHeader titulo="Panel operativos" />
				</Grid>

				<Grid item size={{ xs: 12 }}>
					<OperativosFilters
						fechaDesde={fechaDesde}
						fechaHasta={fechaHasta}
						textoBusqueda={textoBusqueda}
						filtroOperativo={filtroOperativo}
						onFechaDesdeChange={setFechaDesde}
						onFechaHastaChange={setFechaHasta}
						onMoverRangoFechas={moverRangoFechas}
						onTextoBusquedaChange={setTextoBusqueda}
						onFiltroOperativoChange={setFiltroOperativo}
						onAplicarFiltroFechas={aplicarFiltroFechas}
						onLimpiarFiltros={limpiarFiltros}
						onVolver={volverACuadrillas}
						onExportarOperativos={exportarPartesOperativos}
						exporting={exporting}
						cantidadFiltros={cantidadFiltros}
						onToggleFiltros={toggleFiltros}
					/>
				</Grid>

				<Grid item size={{ xs: 12 }}>
					<OperativosAdvancedFilters
						open={openFiltros}
						onClose={cerrarFiltros}
						partes={partes}
						loaded={loaded}
						setCantidadFiltros={setCantidadFiltros}
						onFiltersChange={setFiltrosAvanzados}
						initialSelectedFilters={filtrosAvanzados}
						resetToken={resetFiltrosAvanzadosToken}
					/>
				</Grid>
			</Grid>

			<Grid container direction="row" justifyContent="space-between" alignItems="center" size={{ xs: 12, md: 11.5 }} sx={{ mt: 1, mb: 1 }}>
				<Grid item size={{ xs: 12 }}>
					<Typography variant="body2" color="text.secondary">
						Rango aplicado: <strong>{formatDisplayDate(filtroAplicado.fechaDesde)}</strong> -{" "}
						<strong>{formatDisplayDate(filtroAplicado.fechaHasta)}</strong>
					</Typography>
				</Grid>

				{!!errorFechas && (
					<Grid item size={{ xs: 12 }} sx={{ mt: 1 }}>
						<Alert severity="warning">{errorFechas}</Alert>
					</Grid>
				)}

				{!!feedback?.message && (
					<Grid item size={{ xs: 12 }} sx={{ mt: 1 }}>
						<Alert severity={feedback.severity || "info"}>{feedback.message}</Alert>
					</Grid>
				)}
			</Grid>

			<Grid container direction="row" justifyContent="space-between" alignItems="start" size={{ xs: 12, md: 11.5 }}>
				<Grid container direction="row" justifyContent="center" alignItems="center" size={{ xs: 12 }}>
					{loaded ? (
						<Grid
							container
							direction="row"
							justifyContent="start"
							alignItems="start"
							size={{ xs: 12 }}
							className="fade-in barra-scroll"
							sx={{ overflow: "auto", minHeight: "55vh", maxHeight: "70vh" }}
						>
							{partesFiltrados.length > 0 ? (
								<Grid container direction="column" size={{ xs: 12 }} sx={{ gap: 1.5 }}>
									<OperativosSelectionSummary
										allCurrentPageSelected={allCurrentPageSelected}
										disabled={saving}
										someCurrentPageSelected={someCurrentPageSelected}
										onToggleCurrentPageSelection={toggleCurrentPageSelection}
										currentCaboGroupsCount={currentCaboGroups.length}
										currentPartesCount={currentPartes.length}
									/>

									{currentCaboGroups.map((group) => (
										<OperativosCaboAccordion
											key={group.id}
											group={group}
											expanded={Boolean(expandedCabos[group.id])}
											disabled={saving}
											selectedPartes={selectedPartes}
											onToggleExpanded={toggleExpandedCabo}
											onToggleCaboSelection={toggleCaboSelection}
											onToggleParteSelection={toggleParteSelection}
										/>
									))}
								</Grid>
							) : (
								<Alert severity="warning">No se encontraron partes con los filtros actuales.</Alert>
							)}
						</Grid>
					) : (
						<Grid
							container
							direction="row"
							justifyContent="center"
							alignItems="center"
							size={{ xs: 12 }}
							sx={{ py: 3, minHeight: "50vh" }}
							className="fade-in"
						>
							<LinearProgress sx={{ width: "100%" }} />
						</Grid>
					)}
				</Grid>

				<Grid
					container
					direction="row"
					justifyContent="space-between"
					alignItems="end"
					size={{ xs: 12 }}
					sx={{ mt: 1, display: partesFiltrados.length > 0 ? "flex" : "none" }}
				>
					<Grid container direction="row" justifyContent="start" alignItems="center" size={{ xs: 12, md: 3 }}>
						<Grid item size={{ xs: 6 }}>
							<FormControl variant="standard" fullWidth>
								<InputLabel id="operativos-cabos-por-pagina-label">Cabos por pagina</InputLabel>
								<Select
									labelId="operativos-cabos-por-pagina-label"
									value={itemsPerPage}
									label="Cabos por pagina"
									onChange={(event) => {
										setItemsPerPage(Number(event.target.value));
										setCurrentPage(1);
									}}
								>
									<MenuItem value={10}>10</MenuItem>
									<MenuItem value={25}>25</MenuItem>
									<MenuItem value={50}>50</MenuItem>
									<MenuItem value={100}>100</MenuItem>
								</Select>
							</FormControl>
						</Grid>
					</Grid>

					<Pagination
						count={Math.ceil(partesAgrupadosPorCabo.length / itemsPerPage)}
						page={currentPage}
						onChange={(_, value) => setCurrentPage(value)}
						color="primary"
						sx={{
							display:
								partesAgrupadosPorCabo.length > 0 && itemsPerPage < partesAgrupadosPorCabo.length
									? "flex"
									: "none",
						}}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
};
